export const fontFamily = {
  display: '"LF Rubrik", "LF Rubrik Fallback", "Times New Roman", serif',
  heading: '"IBM Plex Sans", sans-serif',
  headingBold: '"IBM Plex Sans Bold", sans-serif',
  base: '"IBM Plex Sans", sans-serif',
};

export const fontWeight = {
  regular: 400,
  bold: 700,
};

export interface ThemeType {
  fontFamily?: string;
  fontWeight?: number;
  fontSize?: string;
  lineHeight?: string;
  textTransform?: string;
  letterSpacing?: string;
}

// Clamp is used to create a fluid typography scale.
// We use this tool to convert https://clamp.font-size.app/
// Maximum viewport width is 1200px
// Minimum viewport width is 376px
// Set mobile/Desktop font size in the calculator
// Convert rem to px tool https://nekocalc.com/px-to-rem-converter

const displayBase = {
  fontWeight: fontWeight.regular,
  fontFamily: fontFamily.display,
};

const headingExtraSmallBase = {
  fontSize: 'clamp(0.875rem, 0.7039rem + 0.7282vw, 1.25rem)', // 14px => 20px
  lineHeight: 'clamp(1.125rem, 0.8169rem + 1.3146vw, 2rem)', // 18px => 32px
  letterSpacing: '-0.05em',
};

const bodyLargeBase = {
  fontFamily: fontFamily.heading,
  fontSize: 'clamp(1.25rem, 1.1359rem + 0.4854vw, 1.5rem)', // 20px => 24px
  lineHeight: 'clamp(1.75rem, 1.6359rem + 0.4854vw, 2rem)', // 28px => 32px
  letterSpacing: '-0.05em',
};

const bodyMediumBase = {
  fontFamily: fontFamily.base,
  fontSize: '1rem', // 16px
  lineHeight: '1.5rem', // 24px
};

const bodySmallBase = {
  fontFamily: fontFamily.base,
  fontSize: '0.875rem', // 14px
  lineHeight: '1.125rem', // 18px
};

const typography = {
  headingXLarge: {
    ...displayBase,
    fontSize: 'clamp(2.875rem, 2.6468rem + 0.9709vw, 3.375rem);', // 46px => 54px
    lineHeight: 'clamp(3.375rem, 3.1468rem + 0.9709vw, 3.875rem);', // 54px => 62px
    letterSpacing: '0.125rem', // 2px
  },
  headingLarge: {
    ...displayBase,
    fontSize: 'clamp(2.375rem, 2.1468rem + 0.9709vw, 2.875rem);', // 38px => 46px
    lineHeight: 'clamp(2.875rem, 2.6468rem + 0.9709vw, 3.375rem);', // 46px => 54px
  },
  headingLargeStable: {
    ...displayBase,
    fontSize: '2rem;', // 32px
    lineHeight: '2.5rem;', // 40px
  },
  headingMedium: {
    fontWeight: fontWeight.bold,
    fontFamily: fontFamily.headingBold,
    fontSize: 'clamp(1.5rem, 1.2718rem + 0.9709vw, 2rem)', // 24px => 32px
    lineHeight: 'clamp(1.875rem, 1.7039rem + 0.7282vw, 2.25rem)', // 30px => 36px
    letterSpacing: '-0.05em',
  },
  headingSmall: {
    fontFamily: fontFamily.headingBold,
    fontWeight: fontWeight.bold,
    fontSize: 'clamp(1.25rem, 1.1359rem + 0.4854vw, 1.5rem)', // 20px => 24px
    lineHeight: 'clamp(1.75rem, 1.6359rem + 0.4854vw, 2rem)', // 28px => 32px
    letterSpacing: '-0.05em',
  },
  // TODO: headingSmall should be regular by default rather than bold
  headingSmallLight: {
    fontFamily: fontFamily.heading,
    fontWeight: fontWeight.regular,
    fontSize: 'clamp(1.25rem, 1.1359rem + 0.4854vw, 1.5rem)', // 20px => 24px
    lineHeight: 'clamp(1.625rem, 1.4539rem + 0.7282vw, 2rem)', // 26px => 32px
    letterSpacing: '-0.05em',
  },
  headingExtraSmall: {
    ...headingExtraSmallBase,
    fontFamily: fontFamily.heading,
    fontWeight: fontWeight.regular,
  },
  headingExtraSmallBold: {
    ...headingExtraSmallBase,
    fontFamily: fontFamily.headingBold,
    fontWeight: fontWeight.regular,
  },
  bodyLarge: {
    ...bodyLargeBase,
    fontWeight: fontWeight.regular,
  },
  bodyLargeStable: {
    fontFamily: fontFamily.headingBold,
    fontWeight: fontWeight.bold,
    fontSize: '1.25rem;', // 20px
    lineHeight: '1.5rem;', // 24px
  },
  bodyLargeBold: {
    ...bodyLargeBase,
    fontFamily: fontFamily.headingBold,
    fontWeight: fontWeight.bold,
  },
  bodyMedium: {
    ...bodyMediumBase,
    fontWeight: fontWeight.regular,
  },
  bodyMediumToSmall: {
    fontFamily: fontFamily.base,
    fontWeight: fontWeight.regular,
    fontSize: 'clamp(0.875rem, 0.818rem + 0.2427vw, 1rem)', // 14px => 16px
    lineHeight: 'clamp(1.125rem, 0.9539rem + 0.7282vw, 1.5rem)', // 18px => 24px
  },
  bodyMediumToSmallBold: {
    fontFamily: fontFamily.headingBold,
    fontWeight: fontWeight.bold,
    fontSize: 'clamp(0.875rem, 0.818rem + 0.2427vw, 1rem)', // 14px => 16px
    lineHeight: 'clamp(1.125rem, 0.9539rem + 0.7282vw, 1.5rem)', // 18px => 24px
  },
  bodyMediumBold: {
    ...bodyMediumBase,
    fontFamily: fontFamily.headingBold,
    fontWeight: fontWeight.bold,
  },
  bodySmall: {
    ...bodySmallBase,
    fontWeight: fontWeight.regular,
  },
  bodySmallBold: {
    ...bodySmallBase,
    fontFamily: fontFamily.headingBold,
    fontWeight: fontWeight.bold,
  },
  caps: {
    fontWeight: fontWeight.bold,
    fontFamily: fontFamily.headingBold,
    fontSize: 'clamp(0.625rem, 0.4688rem + 0.5vw, 0.75rem)', // 10px > 12px
    lineHeight: 'clamp(0.625rem, 0.4688rem + 0.5vw, 0.75rem)', // 10px > 12px
    textTransform: 'uppercase',
    letterSpacing: 'normal',
  },
  capsSmall: {
    fontWeight: fontWeight.bold,
    fontFamily: fontFamily.headingBold,
    fontSize: '0.625rem', // 10px
    lineHeight: '0.625rem', // 10px
    textTransform: 'uppercase',
    letterSpacing: 'normal', // 1px
  },
  quote: {
    fontWeight: fontWeight.regular,
    fontFamily: fontFamily.heading,
    fontSize: 'clamp(1.5rem, 1.2718rem + 0.9709vw, 2rem)', // 24px > 32px
    lineHeight: 'clamp(2rem, 1.7718rem + 0.9709vw, 2.5rem)', // 32px > 40px
  },
} as const;

export default typography;

export type ThemeTypographyKeys = keyof typeof typography;
export type ThemeTypography = Record<ThemeTypographyKeys, ThemeType>;
